import React from 'react';

// Import existing admin components that USER_MANAGER can access
import OverviewTab from '../admin/OverviewTab';
import UserManagementTab from '../admin/UserManagementTab';
import UserApprovalTab from '../admin/UserApprovalTab';
import ReportsTab from '../admin/ReportsTab';

// Import new read-only subscription component
import SubscriptionViewOnlyTab from '../admin/SubscriptionViewOnlyTab';

/**
 * TabRenderer for USER_MANAGER dashboard
 * Only renders tabs that USER_MANAGER role has permission to access
 */
const UserManagerTabRenderer = ({
  activeTab,
  adminData,
  subscriptionPlans,
  userManagement,
  appSettings
}) => {
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <OverviewTab
            stats={adminData.stats}
            recentUsers={adminData.recentUsers}
            recentErrors={adminData.recentErrors}
            loadingStats={adminData.loadingStats}
            appSettings={appSettings}
          />
        );
      
      case 'users':
        return (
          <UserManagementTab
            users={adminData.users || []}
            setUsers={adminData.setUsers}
            loading={adminData.loading || userManagement.loading}
            error={adminData.error}
            showAddUser={userManagement.showAddUser}
            setShowAddUser={userManagement.setShowAddUser}
            editingUser={userManagement.editingUser}
            setEditingUser={userManagement.setEditingUser}
            formData={userManagement.formData}
            setFormData={userManagement.setFormData}
            handleInputChange={(e) => {
              const { name, value } = e.target;
              userManagement.setFormData(prev => ({ ...prev, [name]: value }));
            }}
            handleAddUser={async (userData) => {
              const result = await userManagement.createUser(userData);
              if (result.success) {
                await adminData.fetchUsers(); // Refresh users list
              } else {
                alert(result.message);
              }
              return result;
            }}
            handleUpdateUser={async (userId, userData) => {
              const result = await userManagement.updateUser(userId, userData);
              if (result.success) {
                await adminData.fetchUsers(); // Refresh users list
              } else {
                alert(result.message);
              }
              return result;
            }}
            handleDeleteUser={async (userId) => {
              // USER_MANAGER cannot delete users - this should not be called
              alert('User Managers cannot delete users. Contact a SuperAdmin.');
              return { success: false, message: 'Permission denied' };
            }}
            handleDeactivateUser={async (userId, reason) => {
              const result = await userManagement.deactivateUser(userId, reason);
              if (result.success) {
                await adminData.fetchUsers(); // Refresh users list
                alert('User deactivated successfully');
              } else {
                alert(result.message);
              }
              return result;
            }}
            handleActivateUser={async (userId) => {
              const result = await userManagement.activateUser(userId);
              if (result.success) {
                await adminData.fetchUsers(); // Refresh users list
                alert('User activated successfully');
              } else {
                alert(result.message);
              }
              return result;
            }}
            handleAssignRole={async (userId, role, adminRole, reason) => {
              // USER_MANAGER cannot assign roles - this should not be called
              alert('User Managers cannot assign roles. Contact a SuperAdmin.');
              return { success: false, message: 'Permission denied' };
            }}
            isDeleting={userManagement.isDeleting}
            handleLoginAsUser={() => {
              // Login As feature disabled for USER_MANAGER role
              alert('Login As feature is not available for User Manager role');
            }}
            appSettings={appSettings}
          />
        );
      
      case 'user-approvals':
        return (
          <UserApprovalTab
            pendingUsers={userManagement.pendingUsers}
            loading={userManagement.loading}
            fetchPendingUsers={userManagement.fetchPendingUsers}
            handleApprove={userManagement.handleApprove}
            handleReject={userManagement.handleReject}
          />
        );
      
      case 'subscriptions':
        return (
          <SubscriptionViewOnlyTab
            users={userManagement.users}
            loading={userManagement.loading}
          />
        );
      
      case 'reports':
        return (
          <ReportsTab
            appSettings={appSettings}
          />
        );
      
      default:
        return (
          <div className="p-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <p className="text-yellow-800">
                Tab "{activeTab}" is not available in User Manager mode.
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex-1">
      {renderTabContent()}
    </div>
  );
};

export default UserManagerTabRenderer;